/**
 * Offer Calculation Service
 *
 * API service for the Lead Creation Flow workflow.
 * Handles leads, eligibility calculations, existing loans, and loan portfolios.
 *
 * Base URL: /api/offer-calculation
 */

import axiosInstance from '../config/axios.config';

// ================================
// CONSTANTS & CONFIGURATIONS
// ================================

const BASE_URL = '/offer-calculation';

/**
 * Workflow stage configurations
 */
export const WORKFLOW_STAGES = {
    LEAD_CAPTURE: { id: 0, name: 'lead_capture', label: 'Lead Capture' },
    DOCUMENT_UPLOAD: { id: 1, name: 'document_upload', label: 'Document Upload' },
    LEAD_DETAILS: { id: 2, name: 'lead_details', label: 'Lead Details' },
    OFFER_GENERATION: { id: 3, name: 'offer_generation', label: 'Offer Generation' },
    ELIGIBILITY_CHECK: { id: 4, name: 'eligibility_check', label: 'Eligibility Check' },
    APPLICATION_SUBMISSION: { id: 5, name: 'application_submission', label: 'Application Submission' }
};

/**
 * Employment type options
 */
export const EMPLOYMENT_TYPES = [
    { value: 'salaried', label: 'Salaried', icon: 'pi pi-briefcase' },
    { value: 'self_employed', label: 'Self Employed', icon: 'pi pi-building' },
    { value: 'professional', label: 'Professional', icon: 'pi pi-user' }
];

/**
 * Application status configurations
 */
export const APPLICATION_STATUSES = {
    new_lead: { value: 'new_lead', label: 'New Lead', severity: 'info', icon: 'pi-star' },
    offers_generated: { value: 'offers_generated', label: 'Offers Generated', severity: 'success', icon: 'pi-check' },
    broadcasted: { value: 'broadcasted', label: 'Broadcasted', severity: 'info', icon: 'pi-megaphone' },
    processing: { value: 'processing', label: 'Processing', severity: 'warning', icon: 'pi-spin pi-spinner' },
    bank_submit: { value: 'bank_submit', label: 'Bank Submit', severity: 'info', icon: 'pi-building' },
    bank_queries: { value: 'bank_queries', label: 'Bank Queries', severity: 'warning', icon: 'pi-building' },
    bank_processing: { value: 'bank_processing', label: 'Bank Processing', severity: 'info', icon: 'pi-cog' },
    sanction: { value: 'sanction', label: 'Sanction', severity: 'success', icon: 'pi-check-circle' },
    sanction_queries: { value: 'sanction_queries', label: 'Sanction Queries', severity: 'warning', icon: 'pi-building' },
    disbursal_queries: { value: 'disbursal_queries', label: 'Disbursal Queries', severity: 'warning', icon: 'pi-building' },
    disbursal: { value: 'disbursal', label: 'Disbursal', severity: 'success', icon: 'pi-wallet' },
    bank_rejected: { value: 'bank_rejected', label: 'Bank Rejected', severity: 'danger', icon: 'pi-times-circle' },
    customer_rejected: { value: 'customer_rejected', label: 'Customer Rejected', severity: 'danger', icon: 'pi-user-minus' },
    processor_rejected: { value: 'processor_rejected', label: 'Processor Rejected', severity: 'danger', icon: 'pi-exclamation-triangle' },
    closed: { value: 'closed', label: 'Closed', severity: 'secondary', icon: 'pi-lock' },
};

/**
 * Affordability score configurations
 */
export const AFFORDABILITY_SCORES = {
    excellent: { value: 'excellent', label: 'Excellent', color: '#22C55E', bgColor: '#DCFCE7', minScore: 80 },
    good: { value: 'good', label: 'Good', color: '#84CC16', bgColor: '#ECFCCB', minScore: 60 },
    moderate: { value: 'moderate', label: 'Moderate', color: '#F97316', bgColor: '#FFEDD5', minScore: 40 },
    poor: { value: 'poor', label: 'Poor', color: '#EF4444', bgColor: '#FEE2E2', minScore: 20 },
    very_poor: { value: 'very_poor', label: 'Very Poor', color: '#DC2626', bgColor: '#FEE2E2', minScore: 0 }
};

/**
 * Risk level configurations
 */
export const RISK_LEVELS = {
    low: { value: 'low', label: 'Low Risk', color: '#22C55E', bgColor: '#DCFCE7', icon: 'pi-shield' },
    medium: { value: 'medium', label: 'Medium Risk', color: '#F97316', bgColor: '#FFEDD5', icon: 'pi-exclamation-triangle' },
    high: { value: 'high', label: 'High Risk', color: '#EF4444', bgColor: '#FEE2E2', icon: 'pi-times-circle' }
};

/**
 * Default FOIR parameters
 */
export const DEFAULT_ELIGIBILITY_PARAMS = {
    foir: 50,
    interest_rate: 10.5,
    tenure_months: 240
};

// ================================
// WORKFLOW APIs
// ================================

/**
 * Get workflow configuration
 * @returns {Promise} Workflow config including stages, entry points, statuses
 */
export const getWorkflowConfig = async () => {
    const response = await axiosInstance.get(`${BASE_URL}/workflow/config`);
    return response.data;
};

/**
 * Check eligibility via workflow
 * @param {string} leadId - Lead ID
 * @param {Object} data - Eligibility check data
 * @param {number} data.total_income - Total monthly income
 * @param {number} [data.total_obligations] - Total existing EMI obligations
 * @param {number} [data.foir] - FOIR percentage (default 50)
 * @param {number} [data.interest_rate] - Interest rate (default 10.5)
 * @param {number} [data.tenure_months] - Tenure in months (default 240)
 * @returns {Promise} Eligibility result
 */
export const checkEligibility = async (leadId, data) => {
    const response = await axiosInstance.post(`${BASE_URL}/workflow/${leadId}/check-eligibility`, data);
    return response.data;
};

/**
 * Submit loan application
 * @param {string} leadId - Lead ID
 * @param {Object} data - Application data
 * @param {string} data.offer_id - Selected offer ID
 * @param {string} [data.notes] - Additional notes
 * @returns {Promise} Submission result
 */
export const submitApplication = async (leadId, data) => {
    const response = await axiosInstance.post(`${BASE_URL}/workflow/${leadId}/submit-application`, data);
    return response.data;
};

/**
 * Get workflow dashboard data
 * @returns {Promise} Dashboard statistics
 */
export const getWorkflowDashboard = async () => {
    const response = await axiosInstance.get(`${BASE_URL}/workflow/dashboard`);
    return response.data;
};

/**
 * Calculate EMI for given parameters
 * @param {Object} data - EMI calculation parameters
 * @param {number} data.loan_amount - Loan amount
 * @param {number} data.interest_rate - Annual interest rate
 * @param {number} data.tenure_months - Tenure in months
 * @returns {Promise} EMI calculation result
 */
export const calculateEmi = async (data) => {
    const response = await axiosInstance.post(`${BASE_URL}/workflow/calculate-emi`, data);
    return response.data;
};

/**
 * Get loan types from Central DB
 * @returns {Promise} Loan types list
 */
export const getProductLoanTypes = async () => {
    const response = await axiosInstance.get(`${BASE_URL}/workflow/loan-types`);
    return response.data;
};

/**
 * Get loan products from Central DB
 * @param {Object} [params] - Query parameters
 * @param {string} [params.loan_type_id] - Filter by loan type ID
 * @param {string} [params.search] - Search query
 * @returns {Promise} Loan products list
 */
export const getLoanProducts = async (params = {}) => {
    const response = await axiosInstance.get(`${BASE_URL}/workflow/loan-products`, { params });
    return response.data;
};

/**
 * Get form template for a specific loan product
 * @param {string} productId - Product UUID
 * @returns {Promise} Form template with sections and fields
 */
export const getProductFormTemplate = async (productId) => {
    const response = await axiosInstance.get(`${BASE_URL}/workflow/loan-products/${productId}/form-template`);
    return response.data;
};

/**
 * Get product-specific master values for dropdowns
 * Returns only the master values configured in the product's ranges
 * @param {string} productId - Product UUID
 * @returns {Promise} Master values filtered by product configuration
 */
export const getProductMasterValues = async (productId) => {
    const response = await axiosInstance.get(`${BASE_URL}/workflow/loan-products/${productId}/master-values`);
    return response.data;
};

/**
 * Get companies associated with a loan product
 * @param {string} productId - Product UUID
 * @returns {Promise} Companies list
 */
export const getProductCompanies = async (productId) => {
    const response = await axiosInstance.get(`${BASE_URL}/workflow/loan-products/${productId}/companies`);
    return response.data;
};

/**
 * Get distinct lenders configured on a product's ranges.
 * Used to populate the Salary Bank Account dropdown in the Lead Wizard.
 */
export const getProductLenders = async (productId) => {
    const response = await axiosInstance.get(`${BASE_URL}/workflow/loan-products/${productId}/lenders`);
    return response.data;
};

/**
 * Get the ABB sampling days configured on a product's ranges (`abb_dates` in
 * the rate-card CSV). Needed by the Bank Statement drawer, which computes a
 * preview ABB before any range has been matched — without these it would fall
 * back to 5,10,15,25 and disagree with the offer engine.
 *
 * Returns { abb_dates: number[], source, variants[], is_ambiguous }.
 */
export const getProductAbbDates = async (productId) => {
    const response = await axiosInstance.get(`${BASE_URL}/workflow/loan-products/${productId}/abb-dates`);
    return response.data;
};

/**
 * Search companies for a loan product (lazy loading for dropdowns)
 * @param {string} productId - Product UUID
 * @param {string} search - Search query
 * @param {number} [limit=30] - Results limit
 * @returns {Promise} Matching companies
 */
/**
 * Search companies for a loan product.
 *
 * Accepts an optional AbortSignal so the caller can cancel an in-flight request
 * when the user types another character — prevents stale results overwriting fresh
 * ones, and frees up HTTP connections on slow networks / large datasets.
 *
 * Usage:
 *   const controller = new AbortController();
 *   await searchProductCompanies(productId, 'tata', 30, controller.signal);
 *   // later: controller.abort() to cancel if still in flight
 */
export const searchProductCompanies = async (productId, search, limit = 30, signal = null) => {
    const response = await axiosInstance.get(`${BASE_URL}/workflow/loan-products/${productId}/companies/search`, {
        params: { search, limit },
        signal, // axios honours AbortController signals natively
    });
    return response.data;
};

/**
 * Fetch companies by their IDs (for restoring selections in edit mode)
 * @param {string} productId - Product UUID
 * @param {Array} ids - Array of company IDs
 * @returns {Promise} Companies list
 */
export const getCompaniesByIds = async (productId, ids) => {
    const response = await axiosInstance.get(`${BASE_URL}/workflow/loan-products/${productId}/companies/search`, {
        params: { ids }
    });
    return response.data;
};

// ================================
// LEAD APIs
// ================================

/**
 * Get paginated list of leads
 * @param {Object} [params] - Query parameters
 * @param {number} [params.page] - Page number
 * @param {number} [params.per_page] - Items per page
 * @param {string} [params.status] - Filter by status
 * @param {string} [params.employment_type] - Filter by employment type
 * @param {string} [params.search] - Search query
 * @returns {Promise} Paginated leads
 */
export const getLeads = async (params = {}) => {
    const response = await axiosInstance.get(`${BASE_URL}/leads`, { params });
    return response.data;
};

/**
 * Get single lead details
 * @param {number} leadId - Lead ID
 * @returns {Promise} Lead details
 */
export const getLead = async (leadId) => {
    const response = await axiosInstance.get(`${BASE_URL}/leads/${leadId}`);
    return response.data;
};

/**
 * Initiate a new lead
 * @param {Object} data - Lead data
 * @param {string} data.phone - Phone number
 * @param {string} data.first_name - First name
 * @param {string} data.employment_type - Employment type
 * @param {string} [data.middle_name] - Middle name
 * @param {string} [data.last_name] - Last name
 * @param {string} [data.email] - Email
 * @param {string} [data.entry_point] - Entry point
 * @returns {Promise} Created lead
 */
export const initiateLead = async (data) => {
    const response = await axiosInstance.post(`${BASE_URL}/leads`, data);
    return response.data;
};

/**
 * Update lead stage data
 * @param {number} leadId - Lead ID
 * @param {Object} data - Stage data
 * @param {string} data.stage - Stage name
 * @param {Object} data.data - Stage-specific data
 * @returns {Promise} Updated lead
 */
export const updateLeadStageData = async (leadId, data) => {
    const response = await axiosInstance.put(`${BASE_URL}/leads/${leadId}/stage-data`, data);
    return response.data;
};

/**
 * Advance lead to next stage
 * @param {number} leadId - Lead ID
 * @param {Object} [data] - Optional data
 * @param {boolean} [data.skip_validation] - Skip validation
 * @returns {Promise} Updated lead
 */
export const advanceLeadStage = async (leadId, data = {}) => {
    const response = await axiosInstance.post(`${BASE_URL}/leads/${leadId}/advance-stage`, data);
    return response.data;
};

/**
 * Update lead status
 * @param {number} leadId - Lead ID
 * @param {Object} data - Status data
 * @param {string} data.status - New status
 * @param {string} [data.notes] - Status notes
 * @returns {Promise} Updated lead
 */
export const updateLeadStatus = async (leadId, data) => {
    const response = await axiosInstance.put(`${BASE_URL}/leads/${leadId}/status`, data);
    return response.data;
};

/**
 * Get full lead details with all relations
 * @param {number} leadId - Lead ID
 * @returns {Promise} Full lead details
 */
export const getLeadFullDetails = async (leadId) => {
    const response = await axiosInstance.get(`${BASE_URL}/leads/${leadId}/full-details`);
    return response.data;
};

/**
 * Get all BureauBoxReport rows linked to this lead (used by the verification
 * chip system on the Lead Wizard — to know which APIs have already been
 * fetched for this lead and skip the re-charge prompt).
 *
 * NOTE: this route is NOT under the /offer-calculation prefix used by other
 * lead endpoints in this file — it lives at /api/leads/{id}/reports
 * directly (see routes/tenant.php). axiosInstance has /api as its baseURL,
 * so we pass `/leads/{id}/reports` as a leading-slash absolute path that
 * bypasses BASE_URL.
 *
 * Returns: { success, data: [{ report_id, report_name, report_data,
 *                              api_provider_service_id, status, ... }] }
 */
export const getLeadReports = async (leadId) => {
    const response = await axiosInstance.get(`/leads/${leadId}/reports`);
    return response.data;
};

/**
 * Generate offers for a lead
 * @param {string} leadId - Lead ID
 * @param {Object} data - Offer generation data
 * @param {number} data.amount - Loan amount
 * @param {number} data.tenure_months - Loan tenure in months
 * @param {number} [data.salary] - Monthly salary
 * @param {number} [data.cibil_score] - CIBIL score
 * @returns {Promise} Generated offers
 */
export const generateOffers = async (leadId, data) => {
    const response = await axiosInstance.post(`${BASE_URL}/leads/${leadId}/generate-offers`, data);
    return response.data;
};

/**
 * Discover possible offers based on salary only (no loan amount/tenure required)
 * Returns all matching ranges with min/max loan, tenure, ROI, etc.
 * @param {string} leadId - Lead ID
 * @param {Object} data - Discovery parameters
 * @param {number} data.salary - Monthly net salary
 * @param {Array} [data.selected_company_ids] - Selected employer IDs
 * @returns {Promise} Discovered offers with range details
 */
export const discoverOffers = async (leadId, data) => {
    const response = await axiosInstance.post(`${BASE_URL}/leads/${leadId}/discover-offers`, data);
    return response.data;
};

/**
 * Select an offer for the lead
 * @param {number} leadId - Lead ID
 * @param {Object} data - Offer selection data
 * @param {string} data.offer_id - Offer ID
 * @param {Object} data.offer_data - Offer details
 * @returns {Promise} Updated lead
 */
export const selectOffer = async (leadId, data) => {
    const response = await axiosInstance.post(`${BASE_URL}/leads/${leadId}/select-offer`, data);
    return response.data;
};

/**
 * Unselect the current offer
 * @param {number} leadId - Lead ID
 * @returns {Promise} Updated lead
 */
export const unselectOffer = async (leadId) => {
    const response = await axiosInstance.post(`${BASE_URL}/leads/${leadId}/unselect-offer`);
    return response.data;
};

/**
 * Store verification result
 * @param {number} leadId - Lead ID
 * @param {Object} data - Verification data
 * @param {string} data.type - Verification type (pan, aadhaar, cibil, etc.)
 * @param {string} data.status - Status (pending, verified, failed)
 * @param {Object} [data.data] - Additional verification data
 * @returns {Promise} Updated lead
 */
export const storeVerification = async (leadId, data) => {
    const response = await axiosInstance.post(`${BASE_URL}/leads/${leadId}/verification`, data);
    return response.data;
};

/**
 * Store OCR extracted data
 * @param {number} leadId - Lead ID
 * @param {Object} data - OCR data
 * @param {string} data.document_type - Document type
 * @param {Object} data.extracted_data - Extracted data
 * @returns {Promise} Updated lead
 */
export const storeOcrData = async (leadId, data) => {
    const response = await axiosInstance.post(`${BASE_URL}/leads/${leadId}/ocr-data`, data);
    return response.data;
};

/**
 * Get existing loans for a lead
 * @param {number} leadId - Lead ID
 * @returns {Promise} Existing loans with summary
 */
export const getLeadExistingLoans = async (leadId) => {
    const response = await axiosInstance.get(`${BASE_URL}/leads/${leadId}/existing-loans`);
    return response.data;
};

/**
 * Get eligibility calculation history for a lead
 * @param {number} leadId - Lead ID
 * @returns {Promise} Eligibility calculations
 */
export const getLeadEligibilityHistory = async (leadId) => {
    const response = await axiosInstance.get(`${BASE_URL}/leads/${leadId}/eligibility-history`);
    return response.data;
};

/**
 * Get offer generation history for a lead
 * @param {number} leadId - Lead ID
 * @returns {Promise} Offer generations
 */
export const getLeadOfferHistory = async (leadId) => {
    const response = await axiosInstance.get(`${BASE_URL}/leads/${leadId}/offer-history`);
    return response.data;
};

/**
 * Get loan portfolio for a lead
 * @param {number} leadId - Lead ID
 * @returns {Promise} Loan portfolio
 */
export const getLeadPortfolio = async (leadId) => {
    const response = await axiosInstance.get(`${BASE_URL}/leads/${leadId}/portfolio`);
    return response.data;
};

// ================================
// ELIGIBILITY APIs
// ================================

/**
 * Perform full eligibility calculation with optional save
 * @param {Object} data - Calculation data
 * @param {number} data.lead_id - Lead ID
 * @param {string} data.eligibility_method - Method (SALARY, NET_PROFIT, ABB, etc.)
 * @param {number} data.total_income - Total income
 * @param {number} [data.total_obligations] - Total obligations
 * @param {number} [data.foir] - FOIR percentage
 * @param {number} [data.interest_rate] - Interest rate
 * @param {number} [data.tenure_months] - Tenure months
 * @param {number} [data.requested_amount] - Requested loan amount
 * @param {string} [data.notes] - Notes
 * @param {boolean} [data.save] - Whether to save calculation
 * @returns {Promise} Calculation result
 */
export const calculateEligibility = async (data) => {
    const response = await axiosInstance.post(`${BASE_URL}/eligibility/calculate`, data);
    return response.data;
};

/**
 * Quick eligibility calculation without saving
 * @param {Object} data - Calculation parameters
 * @param {number} data.total_income - Total income
 * @param {number} [data.total_obligations] - Total obligations
 * @param {number} [data.foir] - FOIR percentage
 * @param {number} [data.interest_rate] - Interest rate
 * @param {number} [data.tenure_months] - Tenure months
 * @param {number} [data.requested_amount] - Requested loan amount
 * @returns {Promise} Calculation result
 */
export const quickCalculateEligibility = async (data) => {
    const response = await axiosInstance.post(`${BASE_URL}/eligibility/quick-calculate`, data);
    return response.data;
};

/**
 * Get all eligibility calculations for a lead
 * @param {number} leadId - Lead ID
 * @returns {Promise} Calculations list
 */
export const getLeadEligibilityCalculations = async (leadId) => {
    const response = await axiosInstance.get(`${BASE_URL}/eligibility/lead/${leadId}`);
    return response.data;
};

/**
 * Get single eligibility calculation details
 * @param {string} calculationId - Calculation ID
 * @returns {Promise} Calculation details
 */
export const getEligibilityCalculation = async (calculationId) => {
    const response = await axiosInstance.get(`${BASE_URL}/eligibility/${calculationId}`);
    return response.data;
};

/**
 * Mark eligibility calculation as final
 * @param {string} calculationId - Calculation ID
 * @returns {Promise} Updated calculation
 */
export const markEligibilityAsFinal = async (calculationId) => {
    const response = await axiosInstance.post(`${BASE_URL}/eligibility/${calculationId}/mark-final`);
    return response.data;
};

/**
 * Delete eligibility calculation
 * @param {string} calculationId - Calculation ID
 * @returns {Promise} Deletion result
 */
export const deleteEligibilityCalculation = async (calculationId) => {
    const response = await axiosInstance.delete(`${BASE_URL}/eligibility/${calculationId}`);
    return response.data;
};

/**
 * Get available eligibility methods
 * @returns {Promise} Methods list
 */
export const getEligibilityMethods = async () => {
    const response = await axiosInstance.get(`${BASE_URL}/eligibility/methods/list`);
    return response.data;
};

/**
 * Calculate EMI per Lac
 * @param {Object} data - Calculation parameters
 * @param {number} data.interest_rate - Annual interest rate
 * @param {number} data.tenure_months - Tenure in months
 * @returns {Promise} EMI per lac result
 */
export const calculateEmiPerLac = async (data) => {
    const response = await axiosInstance.post(`${BASE_URL}/eligibility/calculate-emi-per-lac`, data);
    return response.data;
};

// ================================
// EXISTING LOANS APIs
// ================================

/**
 * Get available loan types
 * @returns {Promise} Loan types list
 */
export const getLoanTypes = async () => {
    const response = await axiosInstance.get(`${BASE_URL}/existing-loans/loan-types`);
    return response.data;
};

/**
 * Get existing loans for a lead with summary
 * @param {number} leadId - Lead ID
 * @returns {Promise} Loans and summary
 */
export const getExistingLoans = async (leadId) => {
    const response = await axiosInstance.get(`${BASE_URL}/existing-loans/lead/${leadId}`);
    return response.data;
};

/**
 * Add a new existing loan
 * @param {number} leadId - Lead ID
 * @param {Object} data - Loan data
 * @param {string} data.loan_type - Loan type
 * @param {string} [data.lender_name] - Lender name
 * @param {number} data.original_loan_amount - Original loan amount
 * @param {number} [data.outstanding_loan] - Outstanding amount
 * @param {number} data.interest_rate - Interest rate
 * @param {number} data.emi - EMI amount
 * @param {number} data.tenure_months - Total tenure
 * @param {number} [data.paid_tenure] - Paid tenure
 * @param {number} [data.remaining_tenure] - Remaining tenure
 * @param {string} [data.loan_start_date] - Start date
 * @param {string} [data.loan_end_date] - End date
 * @param {string} [data.source] - Source (manual, cibil, bank_statement)
 * @returns {Promise} Created loan
 */
export const addExistingLoan = async (leadId, data) => {
    const response = await axiosInstance.post(`${BASE_URL}/existing-loans/lead/${leadId}`, data);
    return response.data;
};

/**
 * Bulk save existing loans (replaces existing)
 * @param {number} leadId - Lead ID
 * @param {Object} data - Loans data
 * @param {Array} data.loans - Array of loan objects
 * @returns {Promise} Saved loans
 */
export const bulkSaveExistingLoans = async (leadId, data) => {
    const response = await axiosInstance.post(`${BASE_URL}/existing-loans/lead/${leadId}/bulk`, data);
    return response.data;
};

/**
 * Get total EMI for a lead
 * @param {number} leadId - Lead ID
 * @returns {Promise} EMI totals
 */
export const getExistingLoansTotalEmi = async (leadId) => {
    const response = await axiosInstance.get(`${BASE_URL}/existing-loans/lead/${leadId}/total-emi`);
    return response.data;
};

/**
 * Get single existing loan details
 * @param {number} leadId - Lead ID
 * @param {string} loanId - Loan ID
 * @returns {Promise} Loan details
 */
export const getExistingLoan = async (leadId, loanId) => {
    const response = await axiosInstance.get(`${BASE_URL}/existing-loans/lead/${leadId}/${loanId}`);
    return response.data;
};

/**
 * Update existing loan
 * @param {number} leadId - Lead ID
 * @param {string} loanId - Loan ID
 * @param {Object} data - Updated loan data
 * @returns {Promise} Updated loan
 */
export const updateExistingLoan = async (leadId, loanId, data) => {
    const response = await axiosInstance.put(`${BASE_URL}/existing-loans/lead/${leadId}/${loanId}`, data);
    return response.data;
};

/**
 * Delete existing loan
 * @param {number} leadId - Lead ID
 * @param {string} loanId - Loan ID
 * @returns {Promise} Deletion result
 */
export const deleteExistingLoan = async (leadId, loanId) => {
    const response = await axiosInstance.delete(`${BASE_URL}/existing-loans/lead/${leadId}/${loanId}`);
    return response.data;
};

/**
 * Toggle loan active status
 * @param {number} leadId - Lead ID
 * @param {string} loanId - Loan ID
 * @returns {Promise} Updated loan
 */
export const toggleExistingLoanActive = async (leadId, loanId) => {
    const response = await axiosInstance.post(`${BASE_URL}/existing-loans/lead/${leadId}/${loanId}/toggle-active`);
    return response.data;
};

// ================================
// PORTFOLIO APIs
// ================================

/**
 * Get affordability score options
 * @returns {Promise} Affordability scores
 */
export const getAffordabilityScores = async () => {
    const response = await axiosInstance.get(`${BASE_URL}/portfolio/affordability-scores`);
    return response.data;
};

/**
 * Get risk level options
 * @returns {Promise} Risk levels
 */
export const getRiskLevels = async () => {
    const response = await axiosInstance.get(`${BASE_URL}/portfolio/risk-levels`);
    return response.data;
};

/**
 * Get all portfolios for a lead
 * @param {number} leadId - Lead ID
 * @returns {Promise} Portfolios list
 */
export const getPortfolios = async (leadId) => {
    const response = await axiosInstance.get(`${BASE_URL}/portfolio/lead/${leadId}`);
    return response.data;
};

/**
 * Store manual portfolio entry
 * @param {number} leadId - Lead ID
 * @param {Object} data - Portfolio data
 * @returns {Promise} Created portfolio
 */
export const storePortfolio = async (leadId, data) => {
    const response = await axiosInstance.post(`${BASE_URL}/portfolio/lead/${leadId}`, data);
    return response.data;
};

/**
 * Generate portfolio analysis from lead data
 * @param {number} leadId - Lead ID
 * @param {Object} data - Generation data
 * @param {Object} data.selected_offer - Selected offer details
 * @param {number} data.selected_offer.loan_amount - Loan amount
 * @param {number} data.selected_offer.emi - EMI amount
 * @param {number} [data.selected_offer.interest_rate] - Interest rate
 * @param {number} [data.selected_offer.tenure_months] - Tenure
 * @param {string} [data.selected_offer.product_name] - Product name
 * @param {string} [data.selected_offer.lender_name] - Lender name
 * @returns {Promise} Generated portfolio
 */
export const generatePortfolio = async (leadId, data) => {
    const response = await axiosInstance.post(`${BASE_URL}/portfolio/lead/${leadId}/generate`, data);
    return response.data;
};

/**
 * Get latest portfolio for a lead
 * @param {number} leadId - Lead ID
 * @returns {Promise} Latest portfolio
 */
export const getLatestPortfolio = async (leadId) => {
    const response = await axiosInstance.get(`${BASE_URL}/portfolio/lead/${leadId}/latest`);
    return response.data;
};

/**
 * Get final portfolio for a lead
 * @param {number} leadId - Lead ID
 * @returns {Promise} Final portfolio
 */
export const getFinalPortfolio = async (leadId) => {
    const response = await axiosInstance.get(`${BASE_URL}/portfolio/lead/${leadId}/final`);
    return response.data;
};

/**
 * Get complete portfolio summary with all related data
 * @param {number} leadId - Lead ID
 * @returns {Promise} Complete summary
 */
export const getPortfolioCompleteSummary = async (leadId) => {
    const response = await axiosInstance.get(`${BASE_URL}/portfolio/lead/${leadId}/summary`);
    return response.data;
};

/**
 * Get single portfolio details
 * @param {number} leadId - Lead ID
 * @param {string} portfolioId - Portfolio ID
 * @returns {Promise} Portfolio details
 */
export const getPortfolio = async (leadId, portfolioId) => {
    const response = await axiosInstance.get(`${BASE_URL}/portfolio/lead/${leadId}/${portfolioId}`);
    return response.data;
};

/**
 * Update portfolio
 * @param {number} leadId - Lead ID
 * @param {string} portfolioId - Portfolio ID
 * @param {Object} data - Updated data
 * @returns {Promise} Updated portfolio
 */
export const updatePortfolio = async (leadId, portfolioId, data) => {
    const response = await axiosInstance.put(`${BASE_URL}/portfolio/lead/${leadId}/${portfolioId}`, data);
    return response.data;
};

/**
 * Delete portfolio
 * @param {number} leadId - Lead ID
 * @param {string} portfolioId - Portfolio ID
 * @returns {Promise} Deletion result
 */
export const deletePortfolio = async (leadId, portfolioId) => {
    const response = await axiosInstance.delete(`${BASE_URL}/portfolio/lead/${leadId}/${portfolioId}`);
    return response.data;
};

/**
 * Mark portfolio as final
 * @param {number} leadId - Lead ID
 * @param {string} portfolioId - Portfolio ID
 * @returns {Promise} Updated portfolio
 */
export const markPortfolioAsFinal = async (leadId, portfolioId) => {
    const response = await axiosInstance.post(`${BASE_URL}/portfolio/lead/${leadId}/${portfolioId}/mark-final`);
    return response.data;
};

// ================================
// MASTER VALUE DROPDOWNS (from Central DB)
// ================================

/**
 * Get applicant types dropdown
 * @returns {Promise} List of applicant types
 */
export const getApplicantTypesDropdown = async () => {
    const response = await axiosInstance.get('/superadmin/masters/applicant-types/dropdown');
    return response.data;
};

/**
 * Get employment statuses dropdown
 * @returns {Promise} List of employment statuses
 */
export const getEmploymentStatusesDropdown = async () => {
    const response = await axiosInstance.get('/superadmin/masters/employment-statuses/dropdown');
    return response.data;
};

/**
 * Get employment sub-statuses dropdown
 * @returns {Promise} List of employment sub-statuses
 */
export const getEmploymentSubStatusesDropdown = async () => {
    const response = await axiosInstance.get('/superadmin/masters/employment-sub-statuses/dropdown');
    return response.data;
};

/**
 * Get income types dropdown
 * @returns {Promise} List of income types
 */
export const getIncomeTypesDropdown = async () => {
    const response = await axiosInstance.get('/superadmin/masters/income-types/dropdown');
    return response.data;
};

/**
 * Get CIBIL statuses dropdown
 * @returns {Promise} List of CIBIL statuses
 */
export const getCibilStatusesDropdown = async () => {
    const response = await axiosInstance.get('/superadmin/masters/cibil-statuses/dropdown');
    return response.data;
};

/**
 * Get type of bank accounts dropdown
 * @returns {Promise} List of bank account types
 */
export const getTypeOfBankAccountsDropdown = async () => {
    const response = await axiosInstance.get('/superadmin/masters/type-of-bank-accounts/dropdown');
    return response.data;
};

/**
 * Get nature of business dropdown
 */
export const getNatureOfBusinessDropdown = async () => {
    const response = await axiosInstance.get('/superadmin/masters/nature-of-businesses/dropdown');
    return response.data;
};

/**
 * Get type of business dropdown
 */
export const getTypeOfBusinessDropdown = async () => {
    const response = await axiosInstance.get('/superadmin/masters/type-of-businesses/dropdown');
    return response.data;
};

/**
 * Get current banker dropdown
 */
export const getCurrentBankerDropdown = async () => {
    const response = await axiosInstance.get('/superadmin/masters/current-bankers/dropdown');
    return response.data;
};

/**
 * Get type of residence dropdown
 */
export const getTypeOfResidenceDropdown = async () => {
    const response = await axiosInstance.get('/superadmin/masters/type-of-residences/dropdown');
    return response.data;
};

/**
 * Get type of office dropdown
 */
export const getTypeOfOfficeDropdown = async () => {
    const response = await axiosInstance.get('/superadmin/masters/type-of-offices/dropdown');
    return response.data;
};

/**
 * Resolve documents based on master value selections
 * @param {Object} masterValues - Object with master_type keys and array of selected IDs
 * @returns {Promise} List of resolved documents
 */
export const resolveDocuments = async (masterValues) => {
    const response = await axiosInstance.post('/superadmin/masters/resolve-documents', {
        master_values: masterValues
    });
    return response.data;
};

/**
 * Get additional documents from loan product ranges
 * @param {Array} rangeIds - Array of range UUIDs from generated offers
 * @returns {Promise} List of additional documents from ranges
 */
export const getAdditionalDocumentsFromRanges = async (rangeIds) => {
    if (!rangeIds || rangeIds.length === 0) {
        return { success: true, data: [] };
    }
    const response = await axiosInstance.post('/superadmin/masters/range-additional-documents', {
        range_ids: rangeIds
    });
    return response.data;
};

/**
 * Get additional documents from all active ranges of a loan product
 * @param {string} productId - Product UUID
 * @returns {Promise} List of additional documents from all active ranges
 */
export const getAdditionalDocumentsFromProduct = async (productId) => {
    if (!productId) {
        return { success: true, data: [] };
    }
    const response = await axiosInstance.post('/superadmin/masters/product-additional-documents', {
        product_id: productId
    });
    return response.data;
};

// ================================
// EQUIFAX BUREAU REPORT FUNCTIONS
// ================================

/**
 * Get Equifax service info (credits required)
 * Service ID 1 = Equifax Report
 * @returns {Promise} Service details including credits_required
 */
export const getEquifaxServiceInfo = async () => {
    const response = await axiosInstance.get('/user/report-services/1');
    return response.data;
};

/**
 * Fetch Equifax report for a lead
 * Uses lead's pre-filled data (PAN, Name, DOB, Address, Phone)
 * @param {string} leadId - Lead ID
 * @param {Object} leadData - Lead data with required fields
 * @returns {Promise} Equifax report result
 */
export const fetchEquifaxForLead = async (leadId, leadData) => {
    const response = await axiosInstance.post(`${BASE_URL}/leads/${leadId}/fetch-equifax`, leadData);
    return response.data;
};

/**
 * Get Equifax report for a lead
 * @param {string} leadId - Lead ID
 * @returns {Promise} Equifax report data if exists
 */
export const getLeadEquifaxReport = async (leadId) => {
    const response = await axiosInstance.get(`${BASE_URL}/leads/${leadId}/equifax-report`);
    return response.data;
};

/**
 * Extract CIBIL score and existing loans from Equifax report
 * Supports both legacy cCRResponse format and new Gridlines profile_data format
 * @param {Object} reportData - Raw Equifax report data
 * @returns {Object} Extracted data { cibilScore, existingLoans }
 */
export const extractDataFromEquifaxReport = (reportData) => {
    let data = reportData;

    // Handle nested structures
    if (data?.report_json) {
        data = data.report_json;
    }
    if (data?.api_response) {
        data = data.api_response;
    }
    if (data?.data?.data) {
        data = data.data.data;
    } else if (data?.data) {
        data = data.data;
    }

    // Check which format we have and extract accordingly
    if (data?.profile_data?.account_detail) {
        // NEW Gridlines format with profile_data.account_detail
        return extractFromGridlinesFormat(data);
    } else if (data?.cCRResponse) {
        // Legacy Equifax format with cCRResponse
        return extractFromLegacyFormat(data);
    }

    // Return empty if no recognized format
    return {
        cibilScore: null,
        existingLoans: [],
        totalExistingEmi: 0,
        totalOutstanding: 0
    };
};

/**
 * Extract data from new Gridlines API format (profile_data.account_detail)
 */
const extractFromGridlinesFormat = (data) => {
    let cibilScore = null;
    const existingLoans = [];

    // Extract CIBIL Score from profile_data.score_details
    const scoreDetails = data?.profile_data?.score_details;
    if (Array.isArray(scoreDetails) && scoreDetails[0]?.score) {
        cibilScore = parseInt(scoreDetails[0].score, 10);
    }

    // Extract accounts from profile_data.account_detail
    const accounts = data?.profile_data?.account_detail || [];

    if (Array.isArray(accounts)) {
        accounts.forEach((account, index) => {
            // Check if account is open/active
            const isOpen = (account?.open || '').toLowerCase() === 'yes';
            const accountStatus = (account?.account_status || '').toLowerCase();
            const balance = parseFloat(account?.balance || 0);

            // Include if open and has balance
            const isActive = isOpen || accountStatus.includes('current') || accountStatus.includes('active');

            if (isActive && balance > 0) {
                const loan = {
                    loan_number: index + 1,
                    loan_type: mapAccountTypeToLoanType(account?.account_type || 'Other'),
                    loan_type_display: account?.account_type || 'Other',
                    lender_name: account?.institution || 'Unknown',
                    original_amount: parseFloat(account?.sanction_amount || 0),
                    outstanding_balance: balance,
                    emi: parseFloat(account?.installment_amount || 0),
                    interest_rate: parseFloat(account?.interest_rate || 0),
                    tenure_months: parseInt(account?.repayment_tenure || 0, 10),
                    start_date: account?.date_opened || null,
                    ownership_type: account?.ownership_type || null,
                    is_active: true,
                    source: 'equifax'
                };
                existingLoans.push(loan);
            }
        });
    }

    // Task 5 — enquiries array. Surepass / Gridlines variants seen in the wild:
    // profile_data.enquiry_details (most common) | profile_data.enquiries.
    // countRecentUnsecuredEnquiries() is tolerant of multiple per-item key names too.
    const gridlinesEnquiries =
        data?.profile_data?.enquiry_details
        ?? data?.profile_data?.enquiries
        ?? [];

    return {
        cibilScore,
        existingLoans,
        totalExistingEmi: existingLoans.reduce((sum, loan) => sum + (loan.emi || 0), 0),
        totalOutstanding: existingLoans.reduce((sum, loan) => sum + (loan.outstanding_balance || 0), 0),
        // Task 4 — count of active UNSECURED loans (incl. credit cards).
        // Drives the engine's active_loans filter against range.master_values.active_loans.
        activeUnsecuredCount: countActiveUnsecuredLoans(existingLoans),
        // Task 5 — count of UNSECURED enquiries in last 30 days. Drives the engine's
        // max_unsecured_enquiries filter (range.master_values.max_unsecured_enquiries, e.g. '<5').
        recentUnsecuredEnquiries30d: countRecentUnsecuredEnquiries(gridlinesEnquiries, 30)
    };
};

/**
 * Extract data from legacy Equifax cCRResponse format
 */
const extractFromLegacyFormat = (data) => {
    let cibilScore = null;
    const existingLoans = [];

    // Extract CIBIL/Credit Score
    const scoreInfo = data?.cCRResponse?.cIRReportDataLst?.[0]?.cIRReportData?.scoreDetails?.[0];
    if (scoreInfo?.score) {
        cibilScore = parseInt(scoreInfo.score, 10);
    }
    // Alternative path for score
    if (!cibilScore) {
        const altScore = data?.cCRResponse?.cIRReportDataLst?.[0]?.cIRReportData?.retailAccountDetails?.score;
        if (altScore) {
            cibilScore = parseInt(altScore, 10);
        }
    }

    // Extract existing loans from account details
    const accounts = data?.cCRResponse?.cIRReportDataLst?.[0]?.cIRReportData?.retailAccountDetails || [];

    if (Array.isArray(accounts)) {
        accounts.forEach((account, index) => {
            // Only include active/open accounts
            const accountStatus = account?.accountStatus || '';
            const isActive = accountStatus.toLowerCase().includes('active') ||
                           accountStatus.toLowerCase().includes('open') ||
                           accountStatus === '';

            if (isActive && account?.currentBalance > 0) {
                const loan = {
                    loan_number: index + 1,
                    loan_type: mapAccountTypeToLoanType(account?.accountType || 'Other'),
                    loan_type_display: account?.accountType || 'Other',
                    lender_name: account?.institution || account?.subscriberName || 'Unknown',
                    original_amount: parseFloat(account?.sanctionAmount || account?.highCreditAmount || 0),
                    outstanding_balance: parseFloat(account?.currentBalance || 0),
                    emi: parseFloat(account?.emiAmount || account?.installmentAmount || 0),
                    interest_rate: parseFloat(account?.interestRate || 0),
                    tenure_months: parseInt(account?.tenure || account?.paymentTenure || 0, 10),
                    start_date: account?.dateOpened || account?.accountOpenDate || null,
                    end_date: account?.dateClosed || null,
                    is_active: true,
                    source: 'equifax'
                };
                existingLoans.push(loan);
            }
        });
    }

    // Task 5 — enquiries for legacy format. Common locations:
    // cIRReportData.enquiryDetails | cIRReportData.enquiries | root .inquiries.
    const legacyEnquiries =
        data?.cCRResponse?.cIRReportDataLst?.[0]?.cIRReportData?.enquiryDetails
        ?? data?.cCRResponse?.cIRReportDataLst?.[0]?.cIRReportData?.enquiries
        ?? data?.inquiries
        ?? [];

    return {
        cibilScore,
        existingLoans,
        totalExistingEmi: existingLoans.reduce((sum, loan) => sum + (loan.emi || 0), 0),
        totalOutstanding: existingLoans.reduce((sum, loan) => sum + (loan.outstanding_balance || 0), 0),
        // Task 4 — same count for legacy format. See Gridlines comment above.
        activeUnsecuredCount: countActiveUnsecuredLoans(existingLoans),
        // Task 5 — see Gridlines comment.
        recentUnsecuredEnquiries30d: countRecentUnsecuredEnquiries(legacyEnquiries, 30)
    };
};

/**
 * Loan types considered "unsecured" for the active_loans_count filter (Task 4,
 * roadmap.md). Banks like Bandhan/TATA/SMFG limit borrowers by count of active
 * unsecured tradelines — credit cards INCLUDED per user decision 2026-05-21.
 * Excludes home, vehicle, two-wheeler, gold, LAP (all secured / different limits).
 */
const UNSECURED_LOAN_TYPES = new Set([
    'personal_loan',
    'credit_card',
    'consumer_loan',
    'business_loan',
    'education_loan',
    'overdraft',
]);

/**
 * Count active unsecured loans from a parsed existingLoans array (the same array
 * produced by extractFromGridlinesFormat / extractFromLegacyFormat). Centralized
 * so it stays consistent between the bureau-pull path and the
 * ExistingLoansStep recompute path. Each loan should already have `is_active:true`
 * by the time it lands in the array (parsers filter inactive ones), so we just
 * filter by loan_type membership in the unsecured set.
 *
 * Exported because ExistingLoansStep needs to recompute on add/remove.
 */
export const countActiveUnsecuredLoans = (loans = []) =>
    (loans || []).filter(l => UNSECURED_LOAN_TYPES.has((l?.loan_type || '').toLowerCase())).length;

/**
 * Count enquiries from a bureau response that are (a) on UNSECURED products
 * (incl. credit cards — matches Task 4 unsecured set) and (b) within the last
 * 30 days. Task 5 (2026-05-21). Banks like L&T / Cholamandalam / TATA cap
 * borrowers by recent enquiry count — many recent enquiries signal credit-hunger.
 *
 * Accepts the raw enquiries array directly so we can call this from BOTH the
 * Gridlines path (profile_data.enquiry_details / .enquiries) and the legacy
 * path (cIRReportData.enquiryDetails / .enquiries) — caller picks the right
 * array name for its format.
 *
 * Each enquiry is considered "unsecured" if its purpose string maps to one
 * of the UNSECURED_LOAN_TYPES via mapAccountTypeToLoanType.
 *
 * Exported in case ExistingLoans or another place wants to recompute later.
 */
export const countRecentUnsecuredEnquiries = (enquiries = [], windowDays = 30) => {
    if (!Array.isArray(enquiries) || enquiries.length === 0) return 0;
    const cutoffMs = Date.now() - (windowDays * 24 * 60 * 60 * 1000);
    let count = 0;
    for (const e of enquiries) {
        // Date field varies by format: Gridlines uses `enquiry_date`, legacy uses `dateOfEnquiry`.
        const dateStr = e?.enquiry_date
            ?? e?.dateOfEnquiry
            ?? e?.date
            ?? null;
        if (!dateStr) continue;
        const t = Date.parse(dateStr);
        if (Number.isNaN(t) || t < cutoffMs) continue;
        // Purpose field name varies too — try common variants.
        const purposeRaw = e?.enquiry_purpose
            ?? e?.purpose
            ?? e?.enquiryPurpose
            ?? e?.subscriberName
            ?? '';
        const mapped = mapAccountTypeToLoanType(String(purposeRaw));
        if (UNSECURED_LOAN_TYPES.has(mapped)) count++;
    }
    return count;
};

/**
 * Map Equifax account type to standard loan type
 */
const mapAccountTypeToLoanType = (accountType) => {
    const typeMap = {
        'Personal Loan': 'personal_loan',
        'Housing Loan': 'home_loan',
        'Home Loan': 'home_loan',
        'Auto Loan': 'vehicle_loan',
        'Vehicle Loan': 'vehicle_loan',
        'Two Wheeler': 'two_wheeler_loan',
        'Credit Card': 'credit_card',
        'Consumer Loan': 'consumer_loan',
        'Business Loan': 'business_loan',
        'Education Loan': 'education_loan',
        'Gold Loan': 'gold_loan',
        'Loan Against Property': 'lap',
        'LAP': 'lap'
    };

    // Find matching type (case-insensitive partial match)
    for (const [key, value] of Object.entries(typeMap)) {
        if (accountType.toLowerCase().includes(key.toLowerCase())) {
            return value;
        }
    }
    return 'other';
};

// ================================
// UTILITY FUNCTIONS
// ================================

/**
 * Format currency for display
 * @param {number} amount - Amount to format
 * @param {string} [currency='INR'] - Currency code
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currency = 'INR') => {
    if (amount === null || amount === undefined) return '-';
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency,
        maximumFractionDigits: 0
    }).format(amount);
};

/**
 * Format percentage for display
 * @param {number} value - Percentage value
 * @param {number} [decimals=2] - Decimal places
 * @returns {string} Formatted percentage
 */
export const formatPercentage = (value, decimals = 2) => {
    if (value === null || value === undefined) return '-';
    return `${Number(value).toFixed(decimals)}%`;
};

/**
 * Get stage by ID
 * @param {number} stageId - Stage ID
 * @returns {Object|undefined} Stage config
 */
export const getStageById = (stageId) => {
    return Object.values(WORKFLOW_STAGES).find(stage => stage.id === stageId);
};

/**
 * Get stage by name
 * @param {string} stageName - Stage name
 * @returns {Object|undefined} Stage config
 */
export const getStageByName = (stageName) => {
    return Object.values(WORKFLOW_STAGES).find(stage => stage.name === stageName);
};

/**
 * Calculate progress percentage based on current stage
 * @param {number} currentStage - Current stage ID
 * @returns {number} Progress percentage (0-100)
 */
export const calculateStageProgress = (currentStage) => {
    const totalStages = Object.keys(WORKFLOW_STAGES).length;
    return Math.round(((currentStage + 1) / totalStages) * 100);
};

/**
 * Get status config by value
 * @param {string} status - Status value
 * @returns {Object|undefined} Status config
 */
export const getStatusConfig = (status) => {
    return APPLICATION_STATUSES[status];
};

/**
 * Get affordability score config
 * @param {string} score - Score value
 * @returns {Object|undefined} Score config
 */
export const getAffordabilityScoreConfig = (score) => {
    return AFFORDABILITY_SCORES[score];
};

/**
 * Get risk level config
 * @param {string} level - Risk level value
 * @returns {Object|undefined} Risk level config
 */
export const getRiskLevelConfig = (level) => {
    return RISK_LEVELS[level];
};

// ================================
// DEFAULT EXPORT
// ================================

const offerCalculationService = {
    // Workflow
    getWorkflowConfig,
    checkEligibility,
    submitApplication,
    getWorkflowDashboard,
    calculateEmi,
    getProductLoanTypes,
    getLoanProducts,
    getProductFormTemplate,
    getProductMasterValues,
    getProductCompanies,
    searchProductCompanies,
    getCompaniesByIds,

    // Leads
    getLeads,
    getLead,
    initiateLead,
    updateLeadStageData,
    advanceLeadStage,
    updateLeadStatus,
    getLeadFullDetails,
    getLeadReports,
    generateOffers,
    discoverOffers,
    selectOffer,
    unselectOffer,
    storeVerification,
    storeOcrData,
    getLeadExistingLoans,
    getLeadEligibilityHistory,
    getLeadOfferHistory,
    getLeadPortfolio,

    // Eligibility
    calculateEligibility,
    quickCalculateEligibility,
    getLeadEligibilityCalculations,
    getEligibilityCalculation,
    markEligibilityAsFinal,
    deleteEligibilityCalculation,
    getEligibilityMethods,
    calculateEmiPerLac,

    // Existing Loans
    getLoanTypes,
    getExistingLoans,
    addExistingLoan,
    bulkSaveExistingLoans,
    getExistingLoansTotalEmi,
    getExistingLoan,
    updateExistingLoan,
    deleteExistingLoan,
    toggleExistingLoanActive,

    // Portfolio
    getAffordabilityScores,
    getRiskLevels,
    getPortfolios,
    storePortfolio,
    generatePortfolio,
    getLatestPortfolio,
    getFinalPortfolio,
    getPortfolioCompleteSummary,
    getPortfolio,
    updatePortfolio,
    deletePortfolio,
    markPortfolioAsFinal,

    // Master Value Dropdowns
    getApplicantTypesDropdown,
    getEmploymentStatusesDropdown,
    getEmploymentSubStatusesDropdown,
    getIncomeTypesDropdown,
    getCibilStatusesDropdown,
    getTypeOfBankAccountsDropdown,
    resolveDocuments,
    getAdditionalDocumentsFromRanges,
    getAdditionalDocumentsFromProduct,

    // Equifax Bureau Report
    getEquifaxServiceInfo,
    fetchEquifaxForLead,
    getLeadEquifaxReport,
    extractDataFromEquifaxReport,

    // Utilities
    formatCurrency,
    formatPercentage,
    getStageById,
    getStageByName,
    calculateStageProgress,
    getStatusConfig,
    getAffordabilityScoreConfig,
    getRiskLevelConfig,

    // Constants
    WORKFLOW_STAGES,
    EMPLOYMENT_TYPES,
    APPLICATION_STATUSES,
    AFFORDABILITY_SCORES,
    RISK_LEVELS,
    DEFAULT_ELIGIBILITY_PARAMS
};

export default offerCalculationService;
